import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, Plus } from "lucide-react";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  ACTIVE_STAGES,
  DEAL_PERMISSIONS,
  SUB_STATES_BY_STAGE,
} from "@/types/deals";
import type {
  DealListItemDto,
  DealPipelineStage,
} from "@/types/deals";
import {
  dealKeys,
  describeApiError,
  useDealsList,
} from "@/hooks/deals/useDeals";
import { dealsApi } from "@/api/deals";
import type { PagedResult } from "@/types/api";
import { Button } from "@/design-system/components/ui";
import type { DealCardAction } from "./DealCard";
import {
  KanbanColumn,
  type DroppedDealPayload,
  type KanbanLookups,
  type SubStateBucket,
} from "./KanbanColumn";
import type { DealListQuery } from "@/types/deals";
import { cn } from "@/design-system/lib/utils";

export type { KanbanLookups } from "./KanbanColumn";

const HIDE_EMPTY_STORAGE_KEY = "phoenix.pipeline.hideEmptySubstates";

interface BaseProps {
  query: DealListQuery;
  cardActions?: DealCardAction[];
  lookups?: KanbanLookups;
  /** Optional override for the per-stage empty-state copy. */
  emptyMessage?: string;
}

interface FocusedKanbanProps extends BaseProps {
  /** Single stage to render — used by C1 (Intake) and B1 (PreApproval) dashboards. */
  stage: DealPipelineStage;
}

/** Single-stage Kanban — one wide column with sub-state sections stacked
 *  vertically. Used by the C1 / B1 dashboards. */
export function FocusedKanban({
  stage,
  query,
  cardActions,
  lookups,
  emptyMessage,
}: FocusedKanbanProps) {
  return (
    <KanbanShell
      stages={[stage]}
      query={query}
      cardActions={cardActions}
      lookups={lookups}
      emptyMessage={emptyMessage}
      mode="focused"
    />
  );
}

interface UnifiedKanbanProps extends BaseProps {
  /** Stages to render side-by-side. Defaults to active stages
   *  (Intake → Closed). Pass a subset for filtered views. */
  stages?: DealPipelineStage[];
  /** Caller can wire the "+ New deal" button into the empty-pipeline CTA. */
  onCreateDeal?: () => void;
  canCreate?: boolean;
}

/** Multi-column Kanban — one column per stage, sub-state sections stacked
 *  inside each column. Underwriting → Closed are display-only because the
 *  spec forbids cross-module advancement from /deal-flow/pipeline. */
export function UnifiedKanban({
  stages = ACTIVE_STAGES,
  query,
  cardActions,
  lookups,
  emptyMessage,
  onCreateDeal,
  canCreate,
}: UnifiedKanbanProps) {
  return (
    <KanbanShell
      stages={stages}
      query={query}
      cardActions={cardActions}
      lookups={lookups}
      emptyMessage={emptyMessage}
      onCreateDeal={onCreateDeal}
      canCreate={canCreate}
      mode="unified"
    />
  );
}

interface ShellProps extends BaseProps {
  stages: DealPipelineStage[];
  mode: "unified" | "focused";
  onCreateDeal?: () => void;
  canCreate?: boolean;
}

function KanbanShell({
  stages,
  query,
  cardActions,
  lookups,
  emptyMessage,
  mode,
  onCreateDeal,
  canCreate,
}: ShellProps) {
  const { hasPermission } = useAuth();
  const canAdvance = hasPermission(DEAL_PERMISSIONS.AdvanceStage);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [pendingDealId, setPendingDealId] = useState<string | null>(null);
  const [hideEmpty, setHideEmpty] = useHideEmptySubstates();

  const dealsQuery = useDealsList(query);
  const allDeals = dealsQuery.data?.data ?? [];

  const handleDrop = useCallback(
    async (payload: DroppedDealPayload) => {
      if (!canAdvance) {
        toast.error("You don't have permission to move deals.");
        return;
      }
      if (pendingDealId === payload.id) return;
      setPendingDealId(payload.id);

      const stageChanged = payload.fromStage !== payload.toStage;

      // Optimistic update — patch every cached deal-list snapshot so the
      // card moves immediately. We capture the previous values to roll
      // back if the stage machine rejects the transition.
      await queryClient.cancelQueries({ queryKey: dealKeys.lists() });
      const snapshots = queryClient.getQueriesData<PagedResult<DealListItemDto>>({
        queryKey: dealKeys.lists(),
      });
      queryClient.setQueriesData<PagedResult<DealListItemDto>>(
        { queryKey: dealKeys.lists() },
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            data: prev.data.map((d) =>
              d.id === payload.id
                ? {
                    ...d,
                    stage: payload.toStage,
                    subState: payload.toSubState,
                    // NOT a viewer-zone "today": StageEnteredDate is a
                    // DateTimeOffset (on DealListItemDto) written server-side as
                    // DateTimeOffset.UtcNow, and every reader parses it as an
                    // instant (daysSince, getTime, dateTime). A day string here
                    // badges a just-dragged card "1d" instead of "0d" after
                    // 17:00 Phoenix. The UTC instant is the correct value.
                    stageEnteredDate: stageChanged
                      ? new Date().toISOString()
                      : d.stageEnteredDate,
                  }
                : d,
            ),
          };
        },
      );

      try {
        if (stageChanged) {
          await dealsApi.advanceStage(payload.id, {
            targetStage: payload.toStage,
            targetSubState: payload.toSubState,
          });
        } else {
          await dealsApi.advanceSubState(payload.id, {
            targetSubState: payload.toSubState,
          });
        }
        queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
        queryClient.invalidateQueries({ queryKey: dealKeys.detail(payload.id) });
      } catch (err) {
        for (const [key, value] of snapshots) {
          queryClient.setQueryData(key, value);
        }
        const detail = describeApiError(
          err,
          "Could not move deal — refresh and try again.",
        );
        toast.error(`Cannot advance: ${detail}`);
      } finally {
        setPendingDealId(null);
      }
    },
    [canAdvance, pendingDealId, queryClient, toast],
  );

  const columns = useMemo(
    () => buildColumns(stages, allDeals),
    [stages, allDeals],
  );

  if (dealsQuery.isLoading) {
    return (
      <div className="text-small text-gray-mid italic px-space-2">
        Loading deals…
      </div>
    );
  }
  if (dealsQuery.isError) {
    const message =
      dealsQuery.error instanceof ApiError
        ? dealsQuery.error.errors.join("; ") || dealsQuery.error.message
        : "Could not load deals.";
    return (
      <div className="text-small text-status-error px-space-2">{message}</div>
    );
  }

  // Whole-pipeline-empty CTA. Only shown for the unified board on /deal-flow/pipeline;
  // dashboards keep the per-stage friendly copy because they're scoped to
  // a single column and a single user.
  if (mode === "unified" && allDeals.length === 0) {
    return (
      <div className="flex flex-col gap-space-3">
        <Toolbar hideEmpty={hideEmpty} onToggleHideEmpty={setHideEmpty} />
        <EmptyPipelineCta canCreate={!!canCreate} onCreateDeal={onCreateDeal} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      <Toolbar hideEmpty={hideEmpty} onToggleHideEmpty={setHideEmpty} />
      <div
        className={cn(
          mode === "focused"
            ? "flex"
            : "flex gap-space-3 overflow-x-auto pb-space-3",
        )}
      >
        {columns.map((col) => (
          <KanbanColumn
            key={col.stage}
            stage={col.stage}
            totalCount={col.totalCount}
            sections={col.sections}
            acceptsDrops={
              canAdvance && (col.stage === "Intake" || col.stage === "PreApproval")
            }
            onDrop={handleDrop}
            cardActions={cardActions}
            lookups={lookups}
            hideEmptySubstates={hideEmpty}
            fill={mode === "unified"}
            emptyState={
              emptyMessage ? (
                <div className="text-caption italic text-gray-mid px-space-2 py-space-4 text-center">
                  {emptyMessage}
                </div>
              ) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

interface ColumnData {
  stage: DealPipelineStage;
  totalCount: number;
  sections: SubStateBucket[];
}

function buildColumns(
  stages: DealPipelineStage[],
  deals: DealListItemDto[],
): ColumnData[] {
  // Bucket deals by stage::subState so each column can read its sections in
  // O(1). Each section is sorted by stageEnteredDate ascending — oldest
  // first matches "DaysInStage desc" from spec §5.4.
  const bucketsByKey = new Map<string, DealListItemDto[]>();
  for (const d of deals) {
    const key = `${d.stage}::${d.subState}`;
    const arr = bucketsByKey.get(key) ?? [];
    arr.push(d);
    bucketsByKey.set(key, arr);
  }
  for (const arr of bucketsByKey.values()) {
    arr.sort(
      (a, b) =>
        new Date(a.stageEnteredDate).getTime() -
        new Date(b.stageEnteredDate).getTime(),
    );
  }

  return stages.map<ColumnData>((stage) => {
    const definedSubStates = SUB_STATES_BY_STAGE[stage];
    if (definedSubStates.length === 0) {
      // Display-only stages (Underwriting / Closing / Closed) collect
      // every deal in the stage into a single header-less section.
      const items = deals
        .filter((d) => d.stage === stage)
        .sort(
          (a, b) =>
            new Date(a.stageEnteredDate).getTime() -
            new Date(b.stageEnteredDate).getTime(),
        );
      return {
        stage,
        totalCount: items.length,
        sections: [{ subState: null, items }],
      };
    }
    const sections: SubStateBucket[] = definedSubStates.map((subState) => ({
      subState,
      items: bucketsByKey.get(`${stage}::${subState}`) ?? [],
    }));
    const totalCount = sections.reduce((sum, s) => sum + s.items.length, 0);
    return { stage, totalCount, sections };
  });
}

function Toolbar({
  hideEmpty,
  onToggleHideEmpty,
}: {
  hideEmpty: boolean;
  onToggleHideEmpty: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-end">
      <button
        type="button"
        onClick={() => onToggleHideEmpty(!hideEmpty)}
        className={cn(
          "inline-flex items-center gap-space-1 px-space-2 h-7",
          "text-label font-display uppercase tracking-label rounded-md",
          "border border-border-light",
          hideEmpty
            ? "bg-onyx text-gold"
            : "bg-white-pure text-gray-mid hover:text-onyx",
        )}
        aria-pressed={hideEmpty}
        aria-label="Toggle hide empty sub-states"
      >
        {hideEmpty ? (
          <EyeOff size={12} aria-hidden />
        ) : (
          <Eye size={12} aria-hidden />
        )}
        {hideEmpty ? "Show empty" : "Hide empty"}
      </button>
    </div>
  );
}

function EmptyPipelineCta({
  canCreate,
  onCreateDeal,
}: {
  canCreate: boolean;
  onCreateDeal?: () => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center py-space-7 px-space-5",
        "bg-white-pure border border-border-light rounded-lg shadow-sm",
      )}
    >
      <h2 className="font-display font-bold text-h3 text-onyx m-0 mb-space-2">
        {canCreate
          ? "No deals in your pipeline"
          : "No deals match your current filters"}
      </h2>
      <p className="text-body text-gray-mid m-0 max-w-reading">
        {canCreate
          ? "Click + New deal to get started."
          : "Adjust the filters above or check back when more deals are routed your way."}
      </p>
      {canCreate && onCreateDeal && (
        <div className="mt-space-4">
          <Button onClick={onCreateDeal}>
            <Plus size={16} aria-hidden /> New deal
          </Button>
        </div>
      )}
    </div>
  );
}

function useHideEmptySubstates(): [boolean, (next: boolean) => void] {
  const [hide, setHide] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(HIDE_EMPTY_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Keep this tab in sync if another tab toggles the same key — Kanban is
  // often the user's only open tab, but some power-users keep the dashboard
  // and the pipeline open side-by-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      if (e.key !== HIDE_EMPTY_STORAGE_KEY) return;
      setHide(e.newValue === "1");
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const set = useCallback((next: boolean) => {
    setHide(next);
    try {
      window.localStorage.setItem(HIDE_EMPTY_STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, []);

  return [hide, set];
}

// Re-exported type; nothing imports it from here (there are no stories).
export type { ReactNode };
